import { useRef, useState } from 'react';

import { INACCURACY, WS_INACCURACY } from 'constants/params';
import { RootState } from 'store/store';
import { useAppStore } from 'store/store-hooks';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { getInternalPlayer } from 'components/app/_helpers/_app.helper';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { useShiftResize } from 'components/app/editor/_mediator/editor-clips/use-clips-resize-shift';
import { useResize } from 'components/app/editor/_mediator/editor-clips/use-clips-resize';
import {
  getCellMarkersIndex,
  getCellPayload,
} from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { SB_EVENT } from 'components/app/storyboard/_mediator/sb-const';
import { StoryBoardTracks } from 'components/app/storyboard/_mediator/sb.slice';
import { useSbPlayerContext } from 'components/app/storyboard/sb-player/_mediator/sb-player.provider';
import { isImage } from 'components/app/storyboard/sb-player/_mediator/sb-player.helper';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { ResizePosition, useCellResizePayload } from './use-cell-resize-payload';
import { __useSnapTo, SnapToIndex, SnapToTime } from './__use-snap-to';

const VAR__HANDLE_WIDTH = 5;
const VAR__POS_LEFT = '--position-left';
const VAR__POS_RIGHT = '--position-right';
const SNAP_TO_RANGE: Seconds = 0.1;

export const useCellResize = (clip: Clip | null, cell: Index, track: StoryBoardTracks) => {
  const { dragger } = useDraggerContext();
  const { activePlayerIndex, sbPlayers, sbSetIdle, sbPlayerSeekTo } = useSbPlayerContext();
  const { wsGetCursorFraction } = useAudioTrackContext();
  const { sbSeekTo, sbSelectClip } = useSbCb();
  const getCellResizeCb = useCellResizePayload(clip, cell);
  const resizeShift = useShiftResize(clip, cell);
  const onSnapTo = __useSnapTo(cell);
  const resize = useResize(clip);
  const store = useAppStore();

  const container = useRef<HTMLDivElement>(null);
  const leftHandle = useRef<HTMLDivElement>(null);
  const rightHandle = useRef<HTMLDivElement>(null);
  const resizeEndCb = useRef<() => void>();
  const outsideHandle = useRef(false);

  const [snapLabel, setSnapLabel] = useState<number | null>(null);

  const onResizeStart = (event: React.PointerEvent, position: ResizePosition) => {
    if (!clip) return;
    const { duration, markers, beginnerMode, storyConfig } = selectData(store.getState());

    const resizeCb = getCellResizeCb(position);
    if (!resizeCb || !duration || !markers) return;

    const isImg = isImage(clip.clipId);
    const isLeft = position === 'left';
    const cellPayload = getCellPayload(cell, markers, duration);
    const draggingMarker = isLeft ? cellPayload.leftIndex : cellPayload.rightIndex;
    const oppositeCellTime = isLeft ? cellPayload.rightTime : cellPayload.leftTime;
    const handleEl = isLeft ? leftHandle : rightHandle;
    const handleVar = isLeft ? VAR__POS_LEFT : VAR__POS_RIGHT;
    const containerRect = container.current!.getBoundingClientRect();
    const handleRect = handleEl.current!.getBoundingClientRect();
    const posHandle = isLeft ? handleRect.left : handleRect.left + handleRect.width;
    const startPos = event.clientX;
    const dragOffset = posHandle - startPos;
    const dragFactor = isLeft ? 1 : -1;

    const __snapTo = onSnapTo(isLeft, cellPayload);
    const __snapToRes = onSnapTo(isLeft, cellPayload);
    if (!__snapTo || !__snapToRes) return;

    sbSelectClip(clip.clipId, cell, track);
    if (!isLeft) sbSeekTo(cellPayload.rightTime - WS_INACCURACY);

    let clientX = 0;
    let clientY = 0;
    let playerEndTime = 0;
    let playerDuration = 0;
    let dragEndTime: Seconds = -1;
    let dragEndForced: Seconds = -1;

    const onPlayerInit = (player: HTMLVideoElement) => {
      playerDuration = player!.duration;
      playerEndTime = resizeCb.getWsPlayerEndTime(isLeft ? 0 : playerDuration);
      handleDrag();
    };

    const canplayCb = () => {
      player!.removeEventListener('canplay', canplayCb);
      onPlayerInit(player!);
    };

    const initCb = (e: Event) => {
      const { index } = (e as CustomEvent).detail;
      if (index !== activePlayerIndex.current) return;
      document.removeEventListener(SB_EVENT, initCb);
      onPlayerInit(getInternalPlayer(sbPlayers.current[activePlayerIndex.current])!);
    };

    const player = getInternalPlayer(sbPlayers.current[activePlayerIndex.current]);
    if (player) player.addEventListener('canplay', canplayCb);
    else document.addEventListener(SB_EVENT, initCb);

    const setHandlePosition = (position: number) => {
      handleEl.current!.style.setProperty(handleVar, dragFactor * position + 'px');
    };

    const isOutsideClip = (playerTime: Seconds) => {
      return playerDuration && (isLeft ? playerTime < 0 : playerTime > playerDuration);
    };

    const playerSeekTo = isImg ? (_: number) => {} : (time: number) => sbPlayerSeekTo(activePlayerIndex.current, time);

    const seekTo = (() => {
      let labelListener: SnapToTime = -1;

      return (wsTime: Seconds) => {
        const { closestTime, farthestTime } = __snapToRes.onDrag(wsTime);
        const cursorPosition = resizeCb.getCursorPosition(wsTime);
        playerSeekTo(resizeCb.getWsTimeData(wsTime).clipTime);
        setHandlePosition(cursorPosition - startPos - dragOffset);
        sbSeekTo(wsTime);
        dragEndTime = wsTime;

        if (closestTime !== labelListener) {
          labelListener = closestTime;
          if (closestTime === oppositeCellTime) return;
          const playerTime = !isImg && resizeCb.getWsTimeData(closestTime).clipTime;
          const resultTime = playerTime !== false && isOutsideClip(playerTime) ? farthestTime : closestTime;
          const markerPos = resizeCb.getCursorPosition(resultTime);
          setSnapLabel(markerPos - containerRect.left - (isLeft ? 0 : VAR__HANDLE_WIDTH));
          if (beginnerMode || storyConfig?.snapToBeats) dragEndForced = resultTime;
        }
      };
    })();

    const handleDrag = (() => {
      let outsideListener = false;
      let snapToListener: SnapToTime = -1;
      let isFixed = false;

      return () => {
        const wsTime = wsGetCursorFraction(clientX + dragOffset) * duration;
        const { cellResizeDur, clipTime } = resizeCb.getWsTimeData(wsTime);
        const { closestTime, closestTimeRange } = __snapTo.onDrag(isFixed ? null : wsTime);
        if (cellResizeDur <= 0) {
          seekTo(oppositeCellTime);
          isFixed = true;
          return;
        }
        if (closestTimeRange < SNAP_TO_RANGE) {
          if (closestTime === snapToListener || outsideListener) {
            isFixed = false;
            return;
          }
          snapToListener = closestTime;
          seekTo(closestTime);
          isFixed = true;
          return;
        }
        if (isOutsideClip(clipTime)) {
          if (outsideListener) return;
          if (isLeft) sbSetIdle(Math.abs(clipTime));
          seekTo(playerEndTime);
          outsideListener = true;
          isFixed = true;
          return;
        }
        if (outsideListener && isLeft) sbSetIdle(0);
        isFixed = false;
        outsideListener = false;
        snapToListener = -1;
        seekTo(wsTime);
      };
    })();

    const seekToHandle = () => {
      if (isLeft) sbSelectClip(clip.clipId, cell, track);
      else if (draggingMarker) sbSeekTo(markers[draggingMarker] - INACCURACY * 2);
    };

    const onDrag = (event: PointerEvent | React.PointerEvent) => {
      clientX = event.clientX;
      clientY = event.clientY;
      handleDrag();
    };

    const getSnapToIndex = (wsTime: Seconds): SnapToIndex => {
      if (wsTime === 0 || wsTime === duration) return -1;
      const index = markers.findIndex((markerTime) => markerTime === wsTime);
      if (index === -1) return null;
      return index;
    };

    resizeEndCb.current = () => {
      const { heldKey } = selectData(store.getState());

      if (player) player.removeEventListener('canplay', canplayCb);
      else document.removeEventListener(SB_EVENT, initCb);
      setHandlePosition(0);

      const { top, height } = handleEl.current!.getBoundingClientRect();
      outsideHandle.current = clientY < top || clientY > top + height;

      const time = dragEndForced === -1 ? dragEndTime : dragEndForced;
      const snapTo = getSnapToIndex(time);

      const { leftIndex, rightIndex } = getCellMarkersIndex(cell, markers.length);
      if ((leftIndex !== null && leftIndex === snapTo) || (rightIndex !== null && rightIndex === snapTo)) return;
      if (rightIndex === null && snapTo === -1) return;
      if (leftIndex === null && snapTo === -1) return;

      if (snapTo === null) sbSeekTo(time);

      const straddledMarkers = __snapToRes.getStraddled(snapTo);
      const innerMarkers = __snapToRes.getInnerMarkers(time);

      heldKey === 'Shift' && !isImg
        ? resizeShift({ position, snapTo, straddledMarkers })
        : resize(position, draggingMarker, snapTo, straddledMarkers, innerMarkers);

      seekToHandle();
    };

    dragger(handleEl, EnumDragger.cell).start(event, onDrag);
  };

  const onResizeEnd = (event: React.PointerEvent, position: ResizePosition) => {
    setSnapLabel(null);
    const elem = position === 'left' ? leftHandle : rightHandle;
    dragger(elem, EnumDragger.cell).end(event, resizeEndCb.current!);
  };

  return { snapLabel, container, leftHandle, rightHandle, outsideHandle, onResizeStart, onResizeEnd };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { heldKey, beginnerMode } = state.app;
  const { markers, storyConfig } = state.editor;
  const { duration } = state.audio;
  return { heldKey, beginnerMode, duration, markers, storyConfig };
};
